import classNames from "classnames";
import {
  GetPhoneOptionsResponse,
  PhonesConsentSourceType,
  PhonesConsentStatus,
  ValidationErrors,
} from "../../../lib/api/models";
import { formatInCentralTime } from "../../../lib/formatting";
import Select from "../../common/inputs/Select";
import {
  isConsentStatusRequired,
  PhoneNumberFormInput,
} from "../PhoneNumber.hooks";
import styles from "./ModifyPhoneConsentInfo.module.css";

const strings = {
  title: "Automated Systems Consent",
  subTitle:
    "Can we contact you at this number using an automatic dialer and artificial and prerecorded voice?",
  lastConsent: "Last Consent",
  lastMethod: "Last Method",
  lastUpdate: "Last Update",
  select: "Select",
  updateConsent: "Update Consent",
  updateConsentRequired: "Update Consent*",
  updateConsentAllPhones: "Update Consent on All Phones*",
  method: "Method*",
};

type Props = {
  options: GetPhoneOptionsResponse;
  prevConsentStatus?: PhonesConsentStatus | null;
  prevConsentMethod?: PhonesConsentSourceType | null;
  prevConsentUpdateDate?: Date | null;
  prevActive?: boolean;
  formState: PhoneNumberFormInput;
  generalDispatch: (payload: Partial<PhoneNumberFormInput>) => void;
  validationMessages?: ValidationErrors;
  disabled?: boolean;
  consentRevoke: boolean;
  updateAllConsentLabel: boolean;
};

export default function ModifyPhoneConsentInfo({
  options,
  prevConsentStatus,
  prevConsentMethod,
  prevConsentUpdateDate,
  prevActive,
  formState,
  generalDispatch,
  validationMessages,
  disabled,
  consentRevoke,
  updateAllConsentLabel,
}: Props) {
  return (
    <div className={styles.container}>
      <div className={styles.bold}>{strings.title}</div>
      <p className={classNames(styles.bold, styles.fontSize)}>
        {strings.subTitle}
      </p>
      <div className={styles.content}>
        <div className={classNames(styles.prevConsentInfo, styles.fontSize)}>
          <div className={styles.prevInfoLabels}>
            <div>{strings.lastConsent}</div>
            <div>{strings.lastMethod}</div>
            <div>{strings.lastUpdate}</div>
          </div>
          <div className={styles.bold}>
            <div>
              {prevConsentStatus
                ? options.pecStatusTypes.find((o) => o.id === prevConsentStatus)
                    ?.value
                : "-"}
            </div>
            <div>
              {options.pecSources.find((o) => o.id === prevConsentMethod)
                ?.value ?? "-"}
            </div>
            <div>
              {prevConsentUpdateDate
                ? formatInCentralTime(prevConsentUpdateDate, "MM/dd/yyyy")
                : "-"}
            </div>
          </div>
        </div>
        <Select
          label={
            updateAllConsentLabel
              ? strings.updateConsentAllPhones
              : isConsentStatusRequired(
                    formState,
                    formState.phoneId ? "EDIT" : "ADD",
                    prevConsentStatus,
                    prevActive ?? false,
                  )
                ? strings.updateConsentRequired
                : strings.updateConsent
          }
          options={options.pecStatusTypes
            .filter((o) => o.selectable)
            .map((o) => ({
              key: o.id,
              value: o.id,
              label: o.value,
            }))}
          placeholder={strings.select}
          selectedValue={formState.consentStatus}
          onChange={(v: PhonesConsentStatus) => {
            generalDispatch({
              consentStatus: v,
              consentSourceType:
                v === "PEC_NOT_SPECIFIED_OR_UNKNOWN" ? "OTHER" : undefined,
            });
          }}
          validationMessages={validationMessages?.consentStatus}
          disabled={disabled || consentRevoke}
        />
        {formState.consentStatus &&
        formState.consentStatus !== "PEC_NOT_SPECIFIED_OR_UNKNOWN" ? (
          <>
            <Select
              label={strings.method}
              options={options.pecSources
                .filter((o) => o.selectable)
                .map((o) => ({
                  key: o.id,
                  value: o.id,
                  label: o.value,
                }))}
              placeholder={strings.select}
              selectedValue={formState.consentSourceType}
              onChange={(v: PhonesConsentSourceType) =>
                generalDispatch({ consentSourceType: v })
              }
              validationMessages={validationMessages?.consentSourceType}
              disabled={disabled}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
